import { InfoCircleOutlined, LockOutlined } from '@ant-design/icons'
import { useRequest } from 'ahooks'
import { Button, Drawer, Input, Popconfirm, Table, Tooltip, Typography } from 'antd'
import { ColumnType } from 'antd/es/table'
import { produce } from 'immer'
import { useAtom } from 'jotai'
import { isEqual, omit, pick, uniq } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { getPointPermission, MosaicComponent } from '@/components/permission.component'
import { userInfoAtom, usersAtom } from '@/modules/user'
import { DepartmentPeopleWorkResource, PAGE_RESOURCE_POINTS_ENUM, Team, WorkResourceRowType } from '@/schemas'

import { ClickAwayUserSearch } from '../click-away/click-away-user-search.component'
import { ClickAwayWeekInputNumber } from '../click-away/click-away-week-input-number.component'
import s from '../special-project/special-project-work-resource.module.less'
import {
  getAllKeys,
  getIsValidWeekToUpsert,
  getParentByKey,
  getRecordByKey,
} from '../special-project/special-project-work-resource.util'

export enum RowType {
  Project = 'project',
  SubProject = 'subproject',
  User = 'user',
}

export interface DepartmentPeopleWorkResourceInput extends DepartmentPeopleWorkResource {
  isCreating?: boolean
}

const TdCell = (props: any) => {
  // onMouseEnter, onMouseLeave在数据量多的时候，会严重阻塞表格单元格渲染，严重影响性能
  return <td {...omit(props, ['onMouseEnter', 'onMouseLeave'])} />
}

// const getSpecificProjectUrl = (selfId?: string, resourceType?: ResourceType) => {
//   switch (resourceType) {
//     case ResourceType.CUSTOMER: {
//       return `${window.location.origin}/customer/${selfId}`
//     }
//     case ResourceType.DEVLOP: {
//       return `${window.location.origin}/devlop/${selfId}`
//     }
//     case ResourceType.PRODUCTION: {
//       return `${window.location.origin}/production/${selfId}`
//     }
//     default: {
//       return ''
//     }
//   }
// }

export const DepartmentPeopleWorkResourceContainer: React.FC<{
  team: Team
  weekDate: { year: number; week: number; startDate: string; endDate: string }
  startDate: string
  endDate: string
}> = React.forwardRef(({ team, startDate, endDate, weekDate }, ref) => {
  const { t } = useTranslation()
  const { canUpdate, canDelete } = getPointPermission(PAGE_RESOURCE_POINTS_ENUM.资源管理, team.permissions)

  const [viewer] = useAtom(userInfoAtom)
  const [users] = useAtom(usersAtom)
  const [openDrawer, setOpenDrawer] = React.useState<{ record: DepartmentPeopleWorkResourceInput; readonly: boolean }>()
  const [drawerContent, setDrawerContent] = React.useState('')
  const [expandKeys, setExpandKeys] = React.useState<string[]>([])
  const [records, setRecords] = React.useState<DepartmentPeopleWorkResource[]>([])

  const isSuperAdmin = viewer.isSuperAdmin

  const {
    data,
    refresh,
    loading: getLoading,
  } = useRequest<DepartmentPeopleWorkResource[]>(
    () => ({
      url: `/api/work-resource/getDepartmentWorkResource?${new URLSearchParams({
        departmentId: team.departmentId,
        type: 'people',
        startDate,
        endDate,
      } as any)}`,
      method: 'get',
    }),
    {
      refreshDeps: [startDate, endDate, team],
    }
  )

  const { run: deleteRun, loading: deleteLoading } = useRequest(
    (data) => ({
      url: `/api/work-resource/deleteWorkResource?${new URLSearchParams(data)}`,
      method: 'delete',
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: updateRun, loading: updateLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/updateWorkResource',
      method: 'patch',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  const { run: commentRun, loading: commentLoading } = useRequest(
    (data) => ({
      url: '/api/work-resource/upsertWeekWorkResourceComment',
      method: 'post',
      data,
    }),
    {
      manual: true,
      onSuccess() {
        refresh()
      },
    }
  )

  React.useEffect(() => {
    if (data) {
      setRecords((prev) => {
        // init set data
        if (!prev || prev.length === 0) {
          const record = data.find((d) => d.key === viewer.email)
          if (record) {
            const keys: string[] = []
            getAllKeys([record], keys)
            setExpandKeys((prev) => uniq(prev.concat(keys)))
          }
        }
        return data
      })
    }
  }, [data])

  React.useEffect(() => {
    const week = openDrawer?.record?.weeks?.[0]
    setDrawerContent(week?.comment?.comment || '')
  }, [openDrawer])

  React.useImperativeHandle(ref, () => {
    return {
      refresh,
    }
  })

  const columns: ColumnType<DepartmentPeopleWorkResourceInput>[] = [
    {
      title: t('work.resource.table.title.people'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      render: (value, record) => {
        if (record.type !== WorkResourceRowType.User) {
          return null
        }

        return (
          <ClickAwayUserSearch
            readonly={true}
            required={true}
            isActive={record.isCreating}
            value={value}
            onChange={(v) => {
              setRecords((prev) =>
                produce(prev, (draft) => {
                  const currentRecords: DepartmentPeopleWorkResourceInput[] = []
                  getRecordByKey(record.key, draft, currentRecords)
                  const [currentRecord] = currentRecords

                  if (currentRecord) {
                    currentRecord.email = v
                  }
                })
              )
              if (record.userWorkResourceSelfId) {
                const parents: DepartmentPeopleWorkResourceInput[] = []
                getParentByKey(record.key, records, parents)
                const [parent] = parents

                updateRun({
                  projectWorkResourceSelfId: parent.projectWorkResourceSelfId,
                  userWorkResourceSelfId: record.userWorkResourceSelfId,
                  email: v,
                })
              }
            }}
          />
        )
      },
      shouldCellUpdate: (curr, prev) => curr.email !== prev.email || curr.isCreating !== prev.isCreating,
    },
    // {
    //   title: t('work.resource.table.title.project'),
    //   dataIndex: 'project',
    //   key: 'project',
    //   width: 140,
    //   render: (_, record) => {
    //     const url = getSpecificProjectUrl(record.project?.selfId, record.project?.resourceType)
    //     return (
    //       <div className='flex gap-2 items-baseline'>
    //         <div className='max-w-24 whitespace-nowrap overflow-hidden overflow-ellipsis'>
    //           <Tooltip title={record.project?.name}>
    //             <Typography.Link href={url} ellipsis rel='noopener noreferrer' target='_blank'>
    //               {record.project?.name}
    //             </Typography.Link>
    //           </Tooltip>
    //         </div>
    //       </div>
    //     )
    //   },
    //   shouldCellUpdate: (curr, prev) => !isEqual(curr.project, prev.project),
    // },
    {
      title: t('work.resource.table.title.subproject'),
      dataIndex: 'subproject',
      key: 'subproject',
      width: 450,
      render: (_, record) => {
        if (![WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type)) {
          return null
        }
        return (
          <div className='max-w-[400px]'>
            {record.subproject ? (
              <Tooltip title={record.subproject?.name}>
                <Typography.Link
                  href={`${window.location.origin}/project/${record.subproject?.selfId}`}
                  ellipsis
                  rel='noopener noreferrer'
                  target='_blank'
                >
                  {getPointPermission(PAGE_RESOURCE_POINTS_ENUM.概况_名称, record.subproject.permissions).canRead ? (
                    record.subproject?.name
                  ) : (
                    <MosaicComponent content={record.subproject?.name} />
                  )}
                </Typography.Link>
              </Tooltip>
            ) : (
              <Tooltip title={record.subprojectName}>
                <span>{record.subprojectName}</span>
              </Tooltip>
            )}
          </div>
        )
      },
      shouldCellUpdate: (curr, prev) =>
        !isEqual(curr.subproject, prev.subproject) ||
        curr.subprojectName !== prev.subprojectName ||
        curr.subprojectSelfId !== prev.subprojectSelfId,
    },
    // {
    //   title: t('work.resource.table.title.predictWorkload'),
    //   dataIndex: 'predictWorkload',
    //   key: 'predictWorkload',
    //   width: 120,
    //   render: (value, record) => {
    //     const readonly =
    //       record.isArchived || ![WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type)
    //     return (
    //       <ClickAwayInputNumber
    //         key='predict'
    //         readonly={readonly}
    //         required={false}
    //         isActive={record.isCreating}
    //         value={value}
    //         onUpdate={(v) => {
    //           const parents: DepartmentPeopleWorkResourceInput[] = []
    //           getParentByKey(record.key, records, parents)
    //           const [parent] = parents

    //           updateRun({
    //             projectWorkResourceSelfId: parent.projectWorkResourceSelfId,
    //             userWorkResourceSelfId: record.userWorkResourceSelfId,
    //             predictWorkload: Number(v),
    //           })
    //         }}
    //         onChange={(v) => {
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: DepartmentPeopleWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.predictWorkload = !isNil(v) ? Number(v) : null
    //               }
    //             })
    //           )
    //         }}
    //       />
    //     )
    //   },
    //   shouldCellUpdate: (curr, prev) => {
    //     return Number(curr.predictWorkload) !== Number(prev.predictWorkload) || curr.isCreating !== prev.isCreating
    //   },
    // },
    // {
    //   title: t('work.resource.table.title.actualWorkload'),
    //   dataIndex: 'actualWorkload',
    //   key: 'actualWorkload',
    //   width: 120,
    //   render: (value) => {
    //     return <span className={ss.readonlyText}>{!isNil(value) ? Number(value) : '待评估'}</span>
    //   },
    //   shouldCellUpdate: (curr, prev) =>
    //     Number(curr.actualWorkload) !== Number(prev.actualWorkload) || curr.isCreating !== prev.isCreating,
    // },
    // {
    //   title: t('work.resource.table.title.period'),
    //   dataIndex: 'period',
    //   key: 'period',
    //   width: 160,
    //   shouldCellUpdate: (curr, prev) =>
    //     curr.startDate !== prev.startDate || curr.endDate !== prev.endDate || curr.isCreating !== prev.isCreating,
    //   render: (_, record) => {
    //     const readonly =
    //       record.isArchived || ![WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type)
    //     return (
    //       <ClickAwayRangePicker
    //         required={false}
    //         isActive={record.isCreating}
    //         readonly={readonly}
    //         startDate={record.startDate ? moment(record.startDate) : undefined}
    //         endDate={record.endDate ? moment(record.endDate) : undefined}
    //         onChange={(_, dateStrings) => {
    //           const [startDate, endDate] = dateStrings
    //           setRecords((prev) =>
    //             produce(prev, (draft) => {
    //               const currentRecords: DepartmentPeopleWorkResourceInput[] = []
    //               getRecordByKey(record.key, draft, currentRecords)
    //               const [currentRecord] = currentRecords

    //               if (currentRecord) {
    //                 currentRecord.startDate = startDate
    //                 currentRecord.endDate = endDate
    //               }
    //             })
    //           )
    //           if (record.userWorkResourceSelfId) {
    //             const parents: DepartmentPeopleWorkResourceInput[] = []
    //             getParentByKey(record.key, records, parents)
    //             const [parent] = parents

    //             updateRun({
    //               projectWorkResourceSelfId: parent.projectWorkResourceSelfId,
    //               userWorkResourceSelfId: record.userWorkResourceSelfId,
    //               startDate: startDate,
    //               endDate: endDate,
    //             })
    //           }
    //         }}
    //       />
    //     )
    //   },
    // },
    // {
    //   title: '资源季度分布',
    //   dataIndex: 'quarterWorkload',
    //   key: 'quarterWorkload',
    //   shouldCellUpdate: (curr, prev) =>
    //     !isEqual(curr.quarters, prev.quarters) ||
    //     curr.startDate !== prev.startDate ||
    //     curr.endDate !== prev.endDate ||
    //     curr.isCreating !== prev.isCreating,
    //   render: (_, record) => {
    //     const allQuarters = record.quarters.slice() || []
    //     const validQuartes: { year: string; quarter: string }[] = []
    //     if (!record.startDate && record.endDate) {
    //       const year = moment(record.endDate).year().toString()
    //       const quarter = `Q${moment(record.endDate).quarter().toString()}`
    //       const exist = allQuarters.some((q) => q.year === year && q.quarter === quarter)
    //       validQuartes.push({ year, quarter })
    //       if (!exist) {
    //         allQuarters.push({ year, quarter, workload: null })
    //       }
    //     } else if (!record.endDate && record.startDate) {
    //       const year = moment(record.startDate).year().toString()
    //       const quarter = `Q${moment(record.startDate).quarter().toString()}`
    //       const exist = allQuarters.some((q) => q.year === year && q.quarter === quarter)
    //       validQuartes.push({ year, quarter, workload: null })
    //       if (!exist) {
    //         allQuarters.push({ year, quarter, workload: null })
    //       }
    //     } else if (record.startDate && record.endDate) {
    //       const start = moment(record.startDate)
    //       const end = moment(record.endDate)
    //       let iter = start.clone()
    //       while (iter.isSameOrBefore(end)) {
    //         const year = iter.year().toString()
    //         const quarter = `Q${iter.quarter().toString()}`
    //         const exist = allQuarters.some((q) => q.year === year && q.quarter === quarter)
    //         validQuartes.push({ year, quarter, workload: null })
    //         if (!exist) {
    //           allQuarters.push({ year, quarter, workload: null })
    //         }
    //         iter = iter.add(1, 'quarter').startOf('quarter')
    //       }
    //     }

    //     allQuarters.sort((a, b) => {
    //       const aYear = Number(a.year)
    //       const aQuarter = Number(a.quarter[1])
    //       const bYear = Number(b.year)
    //       const bQuarter = Number(b.quarter[1])
    //       return aYear - bYear === 0 ? aQuarter - bQuarter : aYear - bYear
    //     })

    //     return (
    //       <Row
    //         gutter={[
    //           { xs: 8, sm: 16, md: 24 },
    //           { xs: 4, sm: 8, md: 12 },
    //         ]}
    //       >
    //         {allQuarters.map((quarterField) => {
    //           const isInTimeRange = validQuartes.some(
    //             (q) => q.year === quarterField.year && q.quarter === quarterField.quarter
    //           )
    //           const isArchived = isNumber(quarterField.archivedLevel) && quarterField.archivedLevel > 0
    //           const readonly =
    //             ![WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) || isArchived
    //           return (
    //             <Col key={`${quarterField.year}-${quarterField.quarter}`}>
    //               <ClickAwayQuarterInputNumber
    //                 showLockedIcon={isArchived}
    //                 required={false}
    //                 key={`${quarterField.year}-${quarterField.quarter}`}
    //                 readonly={readonly}
    //                 disabled={!isInTimeRange}
    //                 isActive={record.isCreating}
    //                 year={quarterField.year}
    //                 quarter={quarterField.quarter}
    //                 value={quarterField}
    //                 onUpdate={(value) => {
    //                   if (record.userWorkResourceSelfId) {
    //                     const quarter = { ...quarterField, workload: Number(value) }

    //                     const parents: DepartmentPeopleWorkResourceInput[] = []
    //                     getParentByKey(record.key, records, parents)
    //                     const [parent] = parents

    //                     updateRun({
    //                       projectWorkResourceSelfId: parent.projectWorkResourceSelfId,
    //                       userWorkResourceSelfId: record.userWorkResourceSelfId,
    //                       quarter,
    //                     })
    //                   }
    //                 }}
    //                 onChange={(value) => {
    //                   setRecords((prev) =>
    //                     produce(prev, (draft) => {
    //                       const currentRecords: DepartmentPeopleWorkResourceInput[] = []
    //                       getRecordByKey(record.key, draft, currentRecords)
    //                       const [currentRecord] = currentRecords

    //                       if (currentRecord) {
    //                         const index = currentRecord.quarters.findIndex(
    //                           (q) => q.quarter === quarterField.quarter && q.year === quarterField.year
    //                         )
    //                         if (~index) {
    //                           currentRecord.quarters[index] = {
    //                             ...quarterField,
    //                             workload: Number(value),
    //                           }
    //                         } else {
    //                           currentRecord.quarters = [
    //                             ...currentRecord.quarters,
    //                             { ...quarterField, workload: Number(value) },
    //                           ]
    //                         }
    //                       }
    //                     })
    //                   )
    //                 }}
    //               />
    //             </Col>
    //           )
    //         })}
    //       </Row>
    //     )
    //   },
    // },
    {
      title: t('work.resource.table.title.weekWorkload'),
      dataIndex: 'weekWorkload',
      key: 'weekWorkload',
      width: 160,
      // cell 更新受到 weekDate state 影响，这里不检查更新逻辑
      // shouldCellUpdate: (curr, prev) =>
      //   !isEqual(curr, prev) ||
      //   curr.startDate !== prev.startDate ||
      //   curr.endDate !== prev.endDate ||
      //   curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const week = record.weeks.find(
          (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
        ) || {
          year: weekDate.year.toString(),
          week: weekDate.week.toString(),
          startDate: weekDate.startDate,
          endDate: weekDate.endDate,
          workload: null,
        }

        const shouldWeekBeArchived =
          !getIsValidWeekToUpsert({ year: Number(week.year), week: Number(week.week) }) || week.isArchived
        // 人员视角，填工时和操作在 [WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) 这一层
        const isUserNotEmployee =
          [WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) &&
          !record.isCreating &&
          users.every((u) => u.email !== record.email)
        const readonly =
          record.isArchived ||
          (!record.isCreating && ![WorkResourceRowType.Project, WorkResourceRowType.Custom].includes(record.type)) ||
          !canUpdate ||
          isUserNotEmployee ||
          shouldWeekBeArchived

        const existWeek = record?.weeks?.[0]
        return (
          <div className='flex gap-2 items-center'>
            <ClickAwayWeekInputNumber
              year={weekDate.year}
              week={weekDate.week}
              value={week}
              showLockedIcon={shouldWeekBeArchived}
              readonly={readonly}
              required={false}
              isActive={record.isCreating}
              onUpdate={(value) => {
                if (record.userWorkResourceSelfId) {
                  const weekToUpdate = { ...week, workload: Number(value) }
                  updateRun({
                    projectSelfId: record.subprojectSelfId,
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    week: weekToUpdate,
                  })
                }
              }}
              onChange={(value) => {
                setRecords((prev) =>
                  produce(prev, (draft) => {
                    const currentRecords: DepartmentPeopleWorkResourceInput[] = []
                    getRecordByKey(record.key, draft, currentRecords)
                    const [currentRecord] = currentRecords

                    if (currentRecord) {
                      const index = currentRecord.weeks.findIndex((q) => q.week === week.week && q.year === week.year)
                      if (~index) {
                        currentRecord.weeks[index] = {
                          ...week,
                          workload: Number(value),
                        }
                      } else {
                        currentRecord.weeks = [{ ...week, workload: Number(value) }]
                      }
                    }
                  })
                )
              }}
            />
            {[WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) &&
              existWeek?.comment?.comment && (
                <Tooltip title='备注'>
                  <InfoCircleOutlined
                    className='cursor-pointer'
                    onClick={() => {
                      setOpenDrawer({ record, readonly: true })
                    }}
                  />
                </Tooltip>
              )}
          </div>
        )
      },
    },
    {
      title: t('work.resource.table.title.operation'),
      dataIndex: 'operation',
      key: 'operation',
      width: 120,
      // shouldCellUpdate: (curr, prev) => !isEqual(curr, prev) || curr.isCreating !== prev.isCreating,
      render: (_, record) => {
        const isUserNotEmployee =
          [WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) &&
          !record.isCreating &&
          users.every((u) => u.email !== record.email)

        if ([WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type)) {
          if (record.isArchived || isUserNotEmployee) {
            return <LockOutlined />
          }

          const commentButton = [WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) ? (
            <Button
              type='link'
              onClick={() => {
                setOpenDrawer({ record, readonly: false })
              }}
              disabled={!canUpdate}
            >
              {t('work.resource.comment')}
            </Button>
          ) : null

          const deleteButton = isSuperAdmin ? (
            <Popconfirm
              title={t(`work.resource.${record.type}.deleteConfirm`)}
              onConfirm={() => {
                const data = pick(record, ['userWorkResourceSelfId'])
                const parents: DepartmentPeopleWorkResourceInput[] = []
                getParentByKey(record.key, records, parents)
                const [parent] = parents

                deleteRun({ ...data, projectWorkResourceSelfId: parent.projectWorkResourceSelfId })
              }}
              disabled={!canDelete}
            >
              <Button type='text' danger disabled={!canDelete}>
                {t('remove')}
              </Button>
            </Popconfirm>
          ) : null

          const week = record.weeks.find(
            (week) => week.week === weekDate.week.toString() && week.year === weekDate.year.toString()
          )
          const invisibleButton =
            [WorkResourceRowType.Custom, WorkResourceRowType.Project].includes(record.type) &&
            canUpdate &&
            !record.invisibleDate &&
            !Number(week?.workload) &&
            moment().isBetween(weekDate.startDate, weekDate.endDate, 'date', '[]') ? (
              <Popconfirm
                title={
                  <div className='max-w-[300px]'>
                    <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm1')}</div>
                    <div className='whitespace-pre-wrap'>{t('work.resource.invisible.confirm2')}</div>
                  </div>
                }
                onConfirm={() => {
                  const parents: DepartmentPeopleWorkResourceInput[] = []
                  getParentByKey(record.key, records, parents)
                  const [parent] = parents

                  updateRun({
                    projectWorkResourceSelfId: parent.projectWorkResourceSelfId,
                    userWorkResourceSelfId: record.userWorkResourceSelfId,
                    invisibleDate: moment().startOf('isoWeek').format('YYYY-MM-DD'),
                  })
                }}
              >
                <Button type='link'>{t('work.resource.invisible.title')}</Button>
              </Popconfirm>
            ) : null

          return (
            <div className='flex gap-2'>
              {commentButton}
              {invisibleButton}
              {deleteButton}
            </div>
          )
        }
      },
    },
  ]

  return (
    <>
      <Table
        rowClassName={(record) => {
          if (record.subprojectWorkResourceSelfId) {
            return s.departmentRow
          }
          if (record.projectWorkResourceSelfId) {
            return s.subprojectRow
          }
          return ''
        }}
        components={{
          body: { cell: TdCell },
        }}
        loading={getLoading || deleteLoading || updateLoading}
        pagination={false}
        bordered
        columns={columns}
        dataSource={records}
        expandable={{ expandedRowKeys: expandKeys, onExpandedRowsChange: (keys) => setExpandKeys(keys as string[]) }}
      />
      <Drawer
        title={openDrawer?.readonly ? '备注详情' : '填写备注'}
        placement='right'
        onClose={() => setOpenDrawer(undefined)}
        visible={!!openDrawer}
      >
        <Input.TextArea
          readOnly={openDrawer?.readonly}
          value={drawerContent}
          onChange={(e) => setDrawerContent(e.target.value)}
          placeholder={t('work.resource.commentPlaceholder')}
          autoSize={{ minRows: 8 }}
        />
        {!openDrawer?.readonly && (
          <div className='flex my-4 justify-end'>
            <Button
              loading={commentLoading}
              type='primary'
              onClick={() => {
                const week = openDrawer?.record?.weeks?.[0]

                commentRun({
                  userWorkResourceSelfId: openDrawer?.record?.userWorkResourceSelfId,
                  year: weekDate.year.toString(),
                  week: weekDate.week.toString(),
                  startDate: weekDate.startDate,
                  endDate: weekDate.endDate,
                  userWeekWorkResourceId: week?.id,
                  comment: drawerContent,
                })
                setOpenDrawer(undefined)
              }}
            >
              {t('submit')}
            </Button>
          </div>
        )}
      </Drawer>
    </>
  )
})
